/**
 * "Record a rate audit" — the second HSP child-row write form.
 *
 * Deliberately the same shape as `LogContactForm`: capability-gated with a reason
 * shown, no identity field, submit guarded in the handler, refetch rather than
 * optimistic insert. The first form set the pattern; the risk here is drifting from
 * it, which is how the third form ends up inconsistent with both.
 *
 * What differs is the payload, and one rule that comes with it: the API REFUSES a
 * "rate differs" verdict with no figure, because a finding that says something was
 * wrong and not what cannot be acted on. This form surfaces that as a client-side
 * requirement too, so the auditor is told before the round trip rather than after.
 */
import { useState } from 'react';
import { Modal } from '@/features/crm/components/Modal';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

/**
 * Mirrors RATE_TEST_RESULTS in customer-management-api/src/lib/hsp/rate-test.
 * Duplicated because the two apps share no package for this yet; the API rejects
 * anything it does not know, so the failure mode of drift is a 400 carrying the
 * valid list, not a bad write.
 */
const RESULTS: { value: string; label: string }[] = [
  { value: 'as_agreed', label: 'As agreed' },
  { value: 'rate_differs', label: 'Rate differs' },
  { value: 'not_bookable', label: 'Not bookable' },
  { value: 'commission_differs', label: 'Commission differs' },
  { value: 'other', label: 'Other' },
];

/*
 * SABRE FIRST AND DEFAULTED. Stacie, 2026-09-09: "if [the GDS] could default to
 * Sabre, that would be great. Save us a click each time." Every audit records
 * one, so an empty default was a mandatory click on a field with an obvious
 * answer.
 *
 * "NOT RECORDED" IS GONE from the picker. Jeff argued for that first — "if
 * you're going to do this, you're going to have it. You don't need a not
 * recorded option" — I made the case for keeping it so an unknown GDS stayed
 * sayable, and he reaffirmed. His reasoning holds on inspection: somebody
 * recording an audit necessarily searched *somewhere*, so the honest options
 * are the three GDSs, and "not recorded" was an invitation to skip a field
 * whose answer the auditor always has.
 *
 * It costs nothing in the data. `gds` is still `string | null` on the API and
 * the parser only validates a value that is PRESENT, so historical rows
 * imported without one remain legal and `RateTestLog` still renders them
 * through `GDS_LABELS`. What changed is that the form cannot MINT a new null.
 *
 * Two GDSs is two audits, not a multi-select — one audit is one observation,
 * which is what keeps the history readable (her: "true, yeah, that's true").
 */
const GDS: { value: string; label: string }[] = [
  { value: 'sabre', label: 'Sabre' },
  { value: 'travelport', label: 'Travelport' },
  { value: 'worldspan', label: 'Worldspan' },
];

/** Results the API will reject without the corresponding figure. */
const NEEDS_RATE = 'rate_differs';
const NEEDS_COMMISSION = 'commission_differs';

/**
 * `datetime-local` wants zoneless LOCAL `YYYY-MM-DDTHH:mm`. Built from the local
 * getters, NOT `toISOString().slice(0,16)` — that yields UTC and would pre-fill a
 * time the auditor did not have, six hours out in Denver and silently so.
 */
function localDateTimeValue(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * The `tested_at` half of the request body — `{}` when the control is empty or
 * unparseable. Mirrors the contact form for the same reason: `datetime-local` is
 * clearable, `new Date('').toISOString()` THROWS RangeError mid-submit, and the
 * catch would report "Invalid time value" over a form that looks filled in.
 * Omitting the key lets the server apply its documented default of now.
 */
function isoInstantOf(localValue: string): { tested_at?: string } {
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? {} : { tested_at: d.toISOString() };
}

export interface LogRateTestFormProps {
  agreementId: string;
  onSaved: () => void;
  /** What the trigger shows. Defaults to a "Record a rate audit" button. */
  triggerLabel?: React.ReactNode;
  /** Trigger classes. `btn` by default; a table cell passes a chip's classes. */
  triggerClassName?: string;
  /** Tooltip, for a trigger whose label alone does not say what it does. */
  triggerTitle?: string;
  testId?: string;
}

export function LogRateTestForm({
  agreementId,
  onSaved,
  triggerLabel,
  triggerClassName = 'btn',
  triggerTitle,
  testId = 'log-rate-test',
}: LogRateTestFormProps) {
  const { can } = useMe();
  const canWrite = can('write:supplier');

  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('as_agreed');
  const [gds, setGds] = useState('sabre');
  const [testedAt, setTestedAt] = useState(() => localDateTimeValue());
  const [rateSeen, setRateSeen] = useState('');
  const [commissionSeen, setCommissionSeen] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canWrite) {
    return (
      <p
        data-testid={`${testId}-readonly`}
        style={{
          fontSize: 11,
          color: 'var(--text-secondary)',
          margin: '8px 0 0',
        }}
      >
        You have read-only access to supplier records, so recording a rate audit
        isn&rsquo;t available on this account.
      </p>
    );
  }

  const reset = () => {
    setResult('as_agreed');
    setGds('sabre');
    setTestedAt(localDateTimeValue());
    setRateSeen('');
    setCommissionSeen('');
    setNotes('');
    setError(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard the handler, not only the button: a form submits on Enter from any
    // field and never touches a disabled button. Two identical audits are worse
    // than one — you cannot tell which reading was real.
    if (saving) return;

    // Checked here as well as server-side so the auditor is told before the round
    // trip. The server is still the authority; this only avoids a pointless 400.
    if (result === NEEDS_RATE && !rateSeen.trim()) {
      setError(
        'Recording "rate differs" needs the rate you saw — otherwise the finding cannot be acted on.',
      );
      return;
    }
    if (result === NEEDS_COMMISSION && !commissionSeen.trim()) {
      setError(
        'Recording "commission differs" needs the commission you saw — or use "not bookable" if nothing came back.',
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await apiFetch('/api/btp/rate-tests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agreement_id: agreementId,
          result,
          gds: gds || null,
          // Zoneless local → the correct instant, carrying an explicit offset, which
          // the API requires so it never guesses a timezone. Sending the raw string
          // would have the server read it as UTC and shift every audit by the offset.
          ...isoInstantOf(testedAt),
          // Sent as STRINGS. These land in a NUMERIC, and the parser refuses a
          // float path on purpose — parsing here to "clean up" would reintroduce it.
          rate_seen: rateSeen.trim() || null,
          commission_seen_pct: commissionSeen.trim() || null,
          notes: notes.trim() || null,
        }),
      });
      reset();
      setOpen(false);
      // Refetch: the server recomputes the drift and the failure streak, so the
      // summary cannot disagree with the list.
      onSaved();
    } catch (err) {
      // The API's own message names the field and the valid values.
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  /*
   * THE FORM IS A MODAL, and the trigger is the caller's.
   *
   * It used to expand INLINE inside the drawer, behind its own hairline border.
   * Jeff, 2026-09-09, on where the team's actions should live: "I'm trying to
   * pull the key actions that the team will need to do out of the drawer and
   * make them accessible from the header row so they can move quickly. Moving
   * those workflows to modals makes the drawer much more useful as reference
   * info and allows for us to have cleaner workspace (the clean modals with
   * more space) for when details need to be added."
   *
   * So this form — six fields and a result that can send the agreement to
   * Active — gets the room a drawer section cannot give it, and the Loading
   * tab's Load-state chip can open it directly. The drawer keeps the audit
   * HISTORY, which is the reference half.
   *
   * The border comment below outlived the border: the group is a modal now, so
   * WCAG 1.4.11's boundary contrast is the Modal's problem and not a
   * hand-rolled outline's. Kept in the history rather than the markup.
   */
  const trigger = (
    <button
      className={triggerClassName}
      data-testid={`${testId}-open`}
      onClick={() => setOpen(true)}
      title={triggerTitle}
      type="button"
    >
      {triggerLabel ?? 'Record a rate audit'}
    </button>
  );

  if (!open) return <>{trigger}</>;

  return (
    <>
      {trigger}
      <Modal
        onClose={() => {
          reset();
          setOpen(false);
        }}
        testId={`${testId}-modal`}
        title="Record a rate audit"
      >
        <form data-testid={`${testId}-form`} onSubmit={submit}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 10,
            }}
          >
            <div>
              <label className="field-label" htmlFor={`${testId}-result`}>
                Result
              </label>
              <select
                className="input"
                data-testid={`${testId}-result`}
                id={`${testId}-result`}
                onChange={(e) => setResult(e.target.value)}
                value={result}
              >
                {RESULTS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor={`${testId}-gds`}>
                GDS searched
              </label>
              <select
                className="input"
                data-testid={`${testId}-gds`}
                id={`${testId}-gds`}
                onChange={(e) => setGds(e.target.value)}
                value={gds}
              >
                {GDS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor={`${testId}-when`}>
                Tested at
              </label>
              <input
                className="input"
                data-testid={`${testId}-when`}
                id={`${testId}-when`}
                // The API refuses a future audit, so offering one wastes the round trip.
                max={localDateTimeValue()}
                onChange={(e) => setTestedAt(e.target.value)}
                type="datetime-local"
                value={testedAt}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`${testId}-rate`}>
                Lowest rate seen
              </label>
              <input
                className="input"
                data-testid={`${testId}-rate`}
                id={`${testId}-rate`}
                // `inputMode` rather than type="number": a number input's spinner and
                // locale handling both interfere with a money field, and the value has
                // to reach the API as the string the auditor typed.
                inputMode="decimal"
                onChange={(e) => setRateSeen(e.target.value)}
                placeholder="e.g. 210.00"
                type="text"
                value={rateSeen}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`${testId}-commission`}>
                Commission seen (%)
              </label>
              <input
                className="input"
                data-testid={`${testId}-commission`}
                id={`${testId}-commission`}
                inputMode="decimal"
                onChange={(e) => setCommissionSeen(e.target.value)}
                placeholder="e.g. 10"
                type="text"
                value={commissionSeen}
              />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <label className="field-label" htmlFor={`${testId}-notes`}>
              Notes <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              className="input"
              data-testid={`${testId}-notes`}
              id={`${testId}-notes`}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What you searched, what came back…"
              rows={2}
              style={{ width: '100%', resize: 'vertical' }}
              value={notes}
            />
          </div>

          {error && (
            <p
              data-testid={`${testId}-error`}
              style={{
                fontSize: 12,
                margin: '10px 0 0',
                color: 'var(--text-primary)',
              }}
            >
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn btn-primary"
              data-testid={`${testId}-submit`}
              disabled={saving}
              type="submit"
            >
              {saving ? 'Saving…' : 'Save audit'}
            </button>
            <button
              className="btn"
              data-testid={`${testId}-cancel`}
              disabled={saving}
              onClick={() => {
                reset();
                setOpen(false);
              }}
              type="button"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
